export function convertHexToRgb(hexColor: string): [number, number, number] | null {
  const isValidHexColor = /^#([A-Fa-f0-9]{3}){1,2}$/.test(hexColor);

  if (!isValidHexColor) {
    throw new Error('Invalid HEX color code. Ensure it is a 3 or 6-character code.');
  }

  let red: number, green: number, blue: number;

  if (hexColor.length === 7) {
    red = parseInt(hexColor.slice(1, 3), 16);
    green = parseInt(hexColor.slice(3, 5), 16);
    blue = parseInt(hexColor.slice(5, 7), 16);
  } else {
    red = parseInt(hexColor[1] + hexColor[1], 16);
    green = parseInt(hexColor[2] + hexColor[2], 16);
    blue = parseInt(hexColor[3] + hexColor[3], 16);
  }

  return [red, green, blue];
}

export function convertHexToCmyk(hexColor: string): [number, number, number, number] | null {
  const rgb = convertHexToRgb(hexColor);

  if (!rgb) return null;

  const [red, green, blue] = rgb;
  const maxRgbValue = 255;
  const normalizedRed = red / maxRgbValue;
  const normalizedGreen = green / maxRgbValue;
  const normalizedBlue = blue / maxRgbValue;

  const black = 1 - Math.max(normalizedRed, normalizedGreen, normalizedBlue);

  // Handle pure black edge case
  if (black === 1) return [0, 0, 0, 100];

  const cyan = (1 - normalizedRed - black) / (1 - black);
  const magenta = (1 - normalizedGreen - black) / (1 - black);
  const yellow = (1 - normalizedBlue - black) / (1 - black);

  return [
    Math.round(cyan * 100),
    Math.round(magenta * 100),
    Math.round(yellow * 100),
    Math.round(black * 100),
  ];
}

export function convertHexToRgba(hexColor: string, opacityPercentage: number): string | null {
  const color = convertHexToRgb(hexColor);

  if (!color) {
    return null;
  }

  const [red, green, blue] = color;
  const alpha = opacityPercentage / 100;

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function convertRgbaToHex(red: number, green: number, blue: number, alphaValue = 1): string {
  const alpha = Math.round(alphaValue * 255);
  const redHex = red.toString(16).padStart(2, '0');
  const greenHex = green.toString(16).padStart(2, '0');
  const blueHex = blue.toString(16).padStart(2, '0');
  const alphaHex = alpha.toString(16).padStart(2, '0');

  return `#${redHex}${greenHex}${blueHex}${alphaHex}`.toUpperCase();
}

export function convertRgbaStringToHex(rgbaString: string) {
  if (!rgbaString.includes('rgba') && !rgbaString.includes('rgb')) return;

  const values = rgbaString
    .replace(/^rgba?\(|\s+|\)$/g, '')
    .split(',')
    .map((val) => parseFloat(val));

  if (values.length === 3) {
    values.push(1);
  }

  return convertRgbaToHex(values[0], values[1], values[2], values[3]);
}
